import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import React, { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetClientByIdMutation,
  useGetClientHierarchyById,
} from "../../api/services/client/clientApis";
import { GetClientDetailsResponse } from "../../api/services/client/models";
import { useGetCurrencies } from "../../api/services/program/programApis";
import {
  MuiBox,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiButtonTertiary,
} from "../../components";
import PermissionWrapper from "../../components/permissionWrapper/PermissionWrapper";
import { MODULE_PERMISSIONS, PAGE_URLS } from "../../helpers/constants";
import { COMPONENT_IDS } from "../../helpers/constants/componentIds";
import { isNotEmpty } from "../../helpers/utils";
import { useSiteCore } from "../../hooks/useSiteCore";
import { setCurrencyList } from "../../redux/slices/appState/appStateSlice";
import {
  setCreateClientData,
  setIsEditClient,
} from "../../redux/slices/client/createClientSlice";
import ClientSearchResultHierarchy from "../clientSearch/components/ClientSearchResultHierarchy/ClientSearchResultHierarchy";
import { CreateClientActivityAndDeliverableMain } from "../createClient/components/activitiesAndDeliverables/CreateClientActivityAndDeliverableMain";
import { ClientContactMain } from "../createClient/components/clientContactMain/ClientContactMain";
import { ClientDocumentMain } from "../createClient/components/clientDocumentMain/ClientDocumentMain";
import { ClientSpecificContentMain } from "../createClient/components/clientSpecificContentMain/ClientSpecificContentMain";
import { CreateClientHeader } from "../createClient/components/createClientHeader/CreateClientHeader";
import { CreateClientSemiHeader } from "../createClient/components/createClientSemiHeader/CreateClientSemiHeader";
import useAccordion from "./useAccordionHook";
import ViewBillingAndPurchaseOrder from "./viewBillingAndPurchaseOrder/ViewBillingAndPurchaseOrder";
import { ViewClientWrapper } from "./ViewClient.style";
import { ViewCompanyMailingAddress } from "./viewCompanyMailingAddress/ViewCompanyMailingAddress";
import ViewProfile from "./viewProfile/ViewProfile";

const ViewClient: React.FC = () => {
  const [showHierarchyModal, setShowHierarchyModal] = useState<boolean>(false);
  const clientContactMainRef = useRef<{
    getAvailableContactsApi: () => {};
    handleAddContactClick: () => void;
  }>(null);
  const clientSpecificContentMainRef = useRef<{
    getClientSpecificContentApi: () => {};
  }>(null);

  const navigate = useNavigate();
  const { id: clientID = "" } = useParams();

  const {
    mutateAsync: getClientById,
    data: viewClientData,
    isSuccess: isGetClientByIdSuccess,
  } = useGetClientByIdMutation();

  const {
    mutateAsync: getClientHierarchyData,
    isSuccess: isClientHierarchySuccess,
    data: clientHierarchyResult,
  } = useGetClientHierarchyById();

  useEffect(() => {
    if (isClientHierarchySuccess) {
      setShowHierarchyModal(true);
    }
  }, [isClientHierarchySuccess]);
  const dispatch = useDispatch();
  const { commonLabels } = useSiteCore();

  const initialAccordionState = {
    viewProfile: false,
    viewCompanyMailingAddress: false,
    viewClientContactMain: false,
    viewBillingAndPurchaseOrder: false,
    viewClientDocumentMain: false,
    viewClientSpecificContentMain: false,
    viewClientSpecificActivities: false,
  };

  const {
    expandAll,
    collapseAll,
    accordionState,
    toggleAccordion,
    isAnyExpanded,
  } = useAccordion(initialAccordionState);

  useEffect(() => {
    dispatch(
      setIsEditClient({
        isEdit: false,
      }),
    );
    if (clientID) {
      dispatch(
        setCreateClientData({
          id: clientID,
          clientCode: "",
          clientTypeId: "",
          clientName: "",
        }),
      );
      getClientById(clientID);
    }
  }, [clientID]);

  const { data: currenciesList, isSuccess: isCurrenciesListSuccess } =
    useGetCurrencies();

  useEffect(() => {
    if (isCurrenciesListSuccess && isNotEmpty(currenciesList)) {
      dispatch(setCurrencyList(currenciesList));
    }
  }, [isCurrenciesListSuccess]);

  const getRemoveIcon = () => {
    return <RemoveIcon color={"primary"} />;
  };

  const getAddIcon = () => {
    return <AddIcon color={"primary"} />;
  };

  const handleViewHierarchyClick = () => {
    getClientHierarchyData(clientID);
  };

  const handleEditClient = (clientId: string) => {
    navigate(PAGE_URLS.EDIT_CLIENT, {
      state: { clientId: clientId, from: PAGE_URLS.SEARCH_CLIENT },
    });
  };

  useEffect(() => {
    if (isGetClientByIdSuccess && isNotEmpty(viewClientData)) {
      const { profile }: GetClientDetailsResponse = viewClientData.data;
      const { id, clientTypeId, clientCode, clientName } = profile;
      dispatch(
        setCreateClientData({
          id: id,
          clientCode: clientCode,
          clientTypeId: clientTypeId,
          clientName,
        }),
      );
    }
  }, [isGetClientByIdSuccess, viewClientData]);

  const methods = useForm({
    mode: "onBlur",
  });

  return (
    <FormProvider {...methods}>
      <ViewClientWrapper>
        <CreateClientHeader
          isViewClient
          onEditClientClick={() => handleEditClient(clientID)}
          onViewHierarchyClick={handleViewHierarchyClick}
          isEditableForCountry={viewClientData?.data?.isEditable}
        />
        {viewClientData?.data && (
          <CreateClientSemiHeader
            isBillingConfigurationSaved
            clientCode={viewClientData.data?.profile?.clientCode}
            parentClient={viewClientData.data?.profile?.parentClientName}
            parentClientCode={viewClientData.data?.profile?.parentClientCode}
            clientName={viewClientData.data?.profile?.clientName}
            isEditable={false}
            isAddButtonVisible={false}
          />
        )}

        <MuiBox className="accordion-button-container">
          <MuiButtonTertiary
            id={COMPONENT_IDS.EXPAND_COLLAPSE_BUTTON}
            onClick={isAnyExpanded ? collapseAll : expandAll}
            className="accordion-button"
          >
            {isAnyExpanded ? commonLabels.collapseAll : commonLabels.expandAll}
            {isAnyExpanded ? getRemoveIcon() : getAddIcon()}
          </MuiButtonTertiary>
        </MuiBox>

        {viewClientData?.data && isNotEmpty(viewClientData.data) && (
          <>
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.VIEW_CLIENT_PROFILE_DETAILS}
            >
              {viewClientData?.data.profile && (
                <ViewProfile
                  data={viewClientData?.data.profile}
                  isExpanded={accordionState.viewProfile}
                  onAccordionChange={() => toggleAccordion("viewProfile")}
                />
              )}
            </PermissionWrapper>
            <MuiBox className="separator-space" />
            <PermissionWrapper
              permission={
                MODULE_PERMISSIONS.VIEW_CLIENT_MAILING_ADDRESS_DETAILS
              }
            >
              {viewClientData.data.mailingAddress && (
                <ViewCompanyMailingAddress
                  data={viewClientData.data.mailingAddress}
                  addressCountryName={
                    viewClientData.data.profile.addressCountryName
                  }
                  isExpanded={accordionState.viewCompanyMailingAddress}
                  onAccordionChange={() =>
                    toggleAccordion("viewCompanyMailingAddress")
                  }
                />
              )}
            </PermissionWrapper>
            <MuiBox className="separator-space" />
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.VIEW_CLIENT_CONTACTS}
            >
              {viewClientData.data.profile && (
                <ClientContactMain
                  parentAccordionStatus={accordionState.viewClientContactMain}
                  onAccordionChange={() =>
                    toggleAccordion("viewClientContactMain")
                  }
                  ref={clientContactMainRef}
                  isAddButtonVisible={false}
                />
              )}
            </PermissionWrapper>
            <MuiBox className="separator-space" />
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.VIEW_CLIENT_DOCUMENTS}
            >
              <ClientDocumentMain
                parentAccordionStatus={accordionState.viewClientDocumentMain}
                onAccordionChange={() =>
                  toggleAccordion("viewClientDocumentMain")
                }
                isAddButtonVisible={false}
                isEditable={false}
              />
            </PermissionWrapper>
            <MuiBox className="separator-space" />
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.VIEW_CLIENT_BILLING_CONFIG}
            >
              <ViewBillingAndPurchaseOrder
                parentAccordionStatus={
                  accordionState.viewBillingAndPurchaseOrder
                }
                clientId={clientID}
                onAccordionChange={() =>
                  toggleAccordion("viewBillingAndPurchaseOrder")
                }
              />
            </PermissionWrapper>
            <MuiBox className="separator-space" />
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.VIEW_CLIENT_SPECIFIC_CONTENT}
            >
              <ClientSpecificContentMain
                parentAccordionStatus={
                  accordionState.viewClientSpecificContentMain
                }
                onAccordionChange={() =>
                  toggleAccordion("viewClientSpecificContentMain")
                }
                ref={clientSpecificContentMainRef}
                isAddButtonVisible={false}
              />
            </PermissionWrapper>
            <MuiBox className="separator-space" />
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.VIEW_CLIENT_SPECIFIC_ACTIVITIES}
            >
              <CreateClientActivityAndDeliverableMain
                parentAccordionStatus={
                  accordionState.viewClientSpecificActivities
                }
                onAccordionChange={() =>
                  toggleAccordion("viewClientSpecificActivities")
                }
                isAddButtonVisible={false}
              />
            </PermissionWrapper>

            <MuiBox className="separator-space" />

            <MuiBox className="bottom-button-container">
              <MuiButtonSecondary
                id={COMPONENT_IDS.CLOSE_BUTTON}
                className="bottom-button-close"
                onClick={() => navigate(PAGE_URLS.SEARCH_CLIENT)}
              >
                {commonLabels.close}
              </MuiButtonSecondary>

              <PermissionWrapper
                hasCountryLevelAccess={viewClientData?.data?.isEditable}
                permission={MODULE_PERMISSIONS.EDIT_CLIENT}
              >
                <MuiButtonPrimary
                  id={COMPONENT_IDS.CLOSE_BUTTON}
                  className="bottom-button-edit"
                  onClick={() => handleEditClient(clientID)}
                >
                  {commonLabels.edit}
                </MuiButtonPrimary>
              </PermissionWrapper>
            </MuiBox>

            {showHierarchyModal && (
              <ClientSearchResultHierarchy
                open={showHierarchyModal}
                onClose={() => setShowHierarchyModal(false)}
                hierarchyData={clientHierarchyResult?.data}
                clientName={viewClientData.data.profile.clientName}
              />
            )}
          </>
        )}
      </ViewClientWrapper>
    </FormProvider>
  );
};

export default ViewClient;
